import { createAsyncThunk } from '@reduxjs/toolkit';
import { CATALOG_REFRESH_DELAY } from '~store/utils';
import {
  DesignerRequestVerb,
  FolderRootPath,
  MODEL_TAB_PATH,
  API_PREFIX,
  http,
} from '@qodly/shared';
import { ITab } from '@qodly/shared';
import { ServerModel } from '~store/modules/model/types';

import { ITreeItem } from '~store/modules/explorer/types';
import { AppState, fetchCatalog } from '..';
import { fetchClassesFolderContent, renameFile } from '../explorer/thunks';
import { openModal } from '../modals/thunks';
import { selectTabByReference } from '../root';

type AppThunkOpts<T = string> = {
  state: AppState;
  rejectValue: T;
};

export const fetchModel = createAsyncThunk<ServerModel, void, AppThunkOpts>(
  'models/fetch',
  async () => {
    const modelFile = await http<any>(API_PREFIX, {
      method: 'POST',
      body: JSON.stringify({
        verb: DesignerRequestVerb.GET_INFO,
        path: MODEL_TAB_PATH,
        as: 'json',
        content: true,
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });

    return modelFile.content;
  },
);

export const saveModel = createAsyncThunk<
  {
    date: string;
    ext: string;
    name: string;
    size: number;
    type: string;
    path: string;
  } & { content: ServerModel },
  ITab<ServerModel>,
  AppThunkOpts
>('models/save', async (tab, { getState, dispatch, rejectWithValue }) => {
  const { tabs } = getState().root;
  const found = tabs.find((tab) => tab.path === MODEL_TAB_PATH);
  if (!found) return rejectWithValue('Error while saving the model');
  try {
    const content = await http<{
      date: string;
      ext: string;
      name: string;
      size: number;
      type: string;
    }>(API_PREFIX, {
      method: 'POST',
      body: JSON.stringify({
        verb: 'save',
        path: found.path,
        content: found.content,
      }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });
    setTimeout(() => {
      dispatch(fetchCatalog());
    }, CATALOG_REFRESH_DELAY);
    return {
      ...content,
      path: found.path,
      content: tab.content || {
        toJSON: true,
        dataClasses: [],
        extraProperties: {},
      },
    };
  } catch (e) {
    return rejectWithValue('Error while saving the model');
  }
});

export const openModel = createAsyncThunk<ITab | void, {}, AppThunkOpts>(
  'models/open',
  async (_, { dispatch, getState }) => {
    const { tabs } = getState().root;
    const found = tabs.find((tab) => tab.path === 'models/main');

    if (found) {
      dispatch(selectTabByReference(found));
      return found;
    }

    dispatch(fetchModel());
  },
);

export const tryRenameDataclass = createAsyncThunk<
  void,
  { dataclassId: string; selectionId: string; name: string },
  AppThunkOpts
>('model/rename-dataclass', async ({ dataclassId, selectionId, name }, { getState, dispatch }) => {
  const classesChildren = await dispatch(fetchClassesFolderContent()).then(
    (res) => res.payload,
  ) as ITreeItem[];

  const state = getState() as AppState;

  // check existing files for the dataclass
  const folder = state.explorer.find(
    (item) => item.path === FolderRootPath.CLASSES,
  );

  if (!folder) throw new Error('Classes folder was not found!');

  const ref = {
    class: {
      found: false,
      newNameTaken: false,
      file: null as ITreeItem | null,
      filename_update: name,
    },
    entity: {
      found: false,
      newNameTaken: false,
      file: null as ITreeItem | null,
      filename_update: `${name}Entity`,
    },
    selection: {
      found: false,
      newNameTaken: false,
      file: null as ITreeItem | null,
      filename_update: `${name}Selection`,
    },
  };

  classesChildren?.forEach((file) => {
    if (file.path === `Classes/${dataclassId}___[<QODLY_ID>]`) {
      file.children?.forEach((child) => {
        const _child = { ...child, name: child.info?.filename };
        if (child.info?.filename === dataclassId) {
          ref.class.found = true;
          ref.class.file = _child;
        } else if (child.info?.filename === `${dataclassId}Entity`) {
          ref.entity.found = true;
          ref.entity.file = _child;
        } else if (child.info?.filename === selectionId) {
          ref.selection.found = true;
          ref.selection.file = _child;
        }
      });
    } else {
      if (file.name === ref.class.filename_update)
        ref.class.newNameTaken = true;

      if (file.name === ref.entity.filename_update)
        ref.entity.newNameTaken = true;

      if (file.name === ref.selection.filename_update)
        ref.selection.newNameTaken = true;
    }
  });

  const errors = Object.entries(ref).reduce<
    {
      type: 'warning' | 'error' | 'info';
      message: string;
    }[]
  >((acc, curr) => {
    const [type, reference] = curr;
    if (!reference.found)
      return [
        ...acc,
        {
          type: 'warning',
          message: `${dataclassId} ${type} file was not found`,
        },
      ];
    if (reference.newNameTaken)
      return [
        ...acc,
        {
          type: 'error',
          message: `Cannot rename ${type} file to ${reference.filename_update} because it already exists. The existing file will be used instead!`,
        },
      ];
    return acc;
  }, []);

  if (errors.length > 0) {
    dispatch(
      openModal({
        message:
          'The following errors were encountered during dataclass rename:',
        views: {
          tips: errors,
        },
      }),
    );
  }

  const filtered = Object.values(ref).filter(
    (item) => item.found && item.file && !item.newNameTaken,
  );
  await Promise.all(
    filtered.map(
      async (file) =>
        await dispatch(
          renameFile({ file: file.file!, newname: file.filename_update }),
        ),
    ),
  );
});
